import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Users, Clock, Hand } from 'lucide-react';
import { ammaAssisPage, images } from '../data/mock';

const AmmaAssis = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-medium text-foreground mb-4">
                {ammaAssisPage.title}
              </h1>
              <p className="text-xl text-primary font-heading mb-8">
                {ammaAssisPage.subtitle}
              </p>
              {ammaAssisPage.description.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-muted-foreground leading-relaxed mb-4"
                >
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative">
              <img
                src={ammaAssisPage.heroImage}
                alt="Chaise Amma assis"
                className="w-full h-[550px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-primary/10" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-3 gap-8">
            <div className="flex items-center gap-4 p-6">
              <div className="w-14 h-14 bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Hand className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium text-foreground">
                  Massage habillé
                </h3>
                <p className="text-sm text-muted-foreground">
                  Sur chaise ergonomique
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6">
              <div className="w-14 h-14 bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium text-foreground">
                  15 à 25 minutes
                </h3>
                <p className="text-sm text-muted-foreground">
                  Durée idéale
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-6">
              <div className="w-14 h-14 bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-medium text-foreground">
                  Adapté entreprise
                </h3>
                <p className="text-sm text-muted-foreground">
                  Interventions sur site
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-4">
              À qui s'adresse le Amma assis ?
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Cette technique convient à tous les professionnels souhaitant 
              une pause revitalisante dans leur journée de travail.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {ammaAssisPage.targetAudience.map((audience, index) => (
              <div
                key={index}
                className="p-6 bg-white border border-border text-center card-hover"
              >
                <h3 className="font-heading text-lg font-medium text-foreground mb-2">
                  {audience.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {audience.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 section-gradient">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
                Les bienfaits du Amma assis
              </h2>
              <p className="text-muted-foreground mb-10 leading-relaxed">
                En quelques minutes, le Amma assis procure des effets bénéfiques 
                immédiats sur le corps et l'esprit.
              </p>
              <div className="space-y-6">
                {ammaAssisPage.detailedBenefits.map((benefit, index) => (
                  <div key={index} className="flex gap-4">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-heading text-lg font-medium text-foreground mb-1">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={images.wellness}
                alt="Bien-être au travail"
                className="w-full h-[600px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - ENHANCED */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium text-foreground mb-6">
            Envie d'essayer le Amma assis ?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
            Proposez cette expérience à vos collaborateurs pour une pause 
            bien-être inoubliable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-primary text-white hover:bg-primary/90">
              Demander un devis entreprise
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/prestations" className="btn-primary">
              Voir les prestations
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmmaAssis;
